<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="estrut-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="estrut-material-link-title"
    >
      <div class="estrut-modal">
        <div class="estrut-modal__accent" aria-hidden="true" />

        <header class="estrut-modal__header">
          <div class="estrut-modal__icon estrut-modal__icon--accent">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.75" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
            </svg>
          </div>
          <div class="estrut-modal__titles">
            <h2 id="estrut-material-link-title" class="estrut-modal__title">
              {{ t('estruturacao.lessons.addLink') }}
            </h2>
            <p class="estrut-modal__subtitle">{{ t('estruturacao.lessons.materialLinkFormHint') }}</p>
          </div>
          <button type="button" class="estrut-modal__close" :aria-label="t('common.close')" @click="close">
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </header>

        <form class="estrut-modal__form" @submit.prevent="submit">
          <div class="estrut-modal__body">
            <div class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-material-link-titulo">
                {{ t('estruturacao.lessons.materialLinkTitle') }}
                <span class="estrut-modal__optional">({{ t('common.optional') }})</span>
              </label>
              <input
                id="estrut-material-link-titulo"
                v-model="form.titulo"
                type="text"
                class="estrut-modal__input"
                :placeholder="t('estruturacao.lessons.materialLinkTitlePlaceholder')"
              />
            </div>

            <div class="estrut-modal__field">
              <label class="estrut-modal__label" for="estrut-material-link-url">
                {{ t('estruturacao.lessons.materialLinkUrl') }}
                <span class="estrut-modal__required">*</span>
              </label>
              <div class="estrut-modal__input-wrap">
                <span class="estrut-modal__input-icon" aria-hidden="true">
                  <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                  </svg>
                </span>
                <input
                  id="estrut-material-link-url"
                  v-model="form.url"
                  type="url"
                  class="estrut-modal__input"
                  :placeholder="t('estruturacao.lessons.materialLinkUrlPlaceholder')"
                  required
                  autofocus
                />
              </div>
            </div>

            <p v-if="error" class="estrut-modal__error" role="alert">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              {{ error }}
            </p>
          </div>

          <footer class="estrut-modal__footer">
            <button type="button" class="estrut-modal__btn estrut-modal__btn--secondary" @click="close">
              <svg fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
              {{ t('common.cancel') }}
            </button>
            <button type="submit" class="estrut-modal__btn estrut-modal__btn--primary" :disabled="saving">
              <svg v-if="saving" class="estrut-modal__btn-spinner" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="3" opacity="0.25" />
                <path fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              <svg v-else fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
              </svg>
              {{ saving ? t('common.loading') : t('common.add') }}
            </button>
          </footer>
        </form>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { reactive, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  open: { type: Boolean, default: false }
})

const emit = defineEmits(['close', 'submit'])

const { t } = useI18n()

const saving = ref(false)
const error = ref('')

const form = reactive({
  titulo: '',
  url: ''
})

watch(
  () => props.open,
  (isOpen) => {
    if (!isOpen) return
    error.value = ''
    saving.value = false
    form.titulo = ''
    form.url = ''
  }
)

function close() {
  emit('close')
}

function normalizeUrl(raw) {
  const value = raw?.trim()
  if (!value) return ''
  if (/^https?:\/\//i.test(value)) return value
  return `https://${value}`
}

function isValidUrl(value) {
  try {
    const parsed = new URL(value)
    return parsed.protocol === 'http:' || parsed.protocol === 'https:'
  } catch {
    return false
  }
}

function submit() {
  const url = normalizeUrl(form.url)
  if (!url || !isValidUrl(url)) {
    error.value = t('estruturacao.lessons.validationMaterialUrl')
    return
  }
  error.value = ''
  saving.value = true
  emit('submit', {
    titulo: form.titulo?.trim() || url,
    url
  })
}

defineExpose({
  setSaving: (v) => { saving.value = v },
  setError: (msg) => { error.value = msg; saving.value = false }
})
</script>

<style src="./estruturacao-modals.css"></style>
